/**
 * BUILT-IN workspace definitions (plans/workspace-tabs.md, S1): a workspace
 * is a NAMED dock arrangement; built-ins ship as authored RECIPES — the same
 * addPanel program the dock's old hard-coded default was, as data. Panels are
 * EXPLICIT: a workspace contains exactly what it lists (the View menu can
 * still open more later — that lands in the workspace's saved blob).
 *
 * Authoring a new built-in = adding one object literal to
 * {@link BUILTIN_WORKSPACES}. The dock builds recipes via its
 * `buildWorkspace()` (which owns panel constraints and the scene panel's
 * special options); this file stays pure data + validation so vitest can pin
 * recipe soundness without a dock.
 */

/** every panel the dock can host (mirrors DockLayout's component map) */
export type DockPanelId =
    | "scene" | "hierarchy" | "inspector" | "assets" | "code" | "nodegraph"
    | "aichat" | "resources" | "projects" | "components" | "performance"
    | "bakegroups" | "deployment" | "timeline";

export const DOCK_PANEL_IDS: readonly DockPanelId[] = [
    "scene", "hierarchy", "inspector", "assets", "code", "nodegraph",
    "aichat", "resources", "projects", "components", "performance",
    "bakegroups", "deployment", "timeline",
];

export interface WorkspacePanelSpec {
    /** dock panel id == component id */
    id: DockPanelId;
    title: string;
    /** relative placement; the FIRST panel is the root and needs none */
    position?: { referencePanel: DockPanelId; direction: "left" | "right" | "above" | "below" | "within" };
    /** BUILD-TIME sizing only (Marcel 2026-08-19): `width`/`height` are the
     *  intended start sizes, applied AFTER the tree is complete; values ≤ 1
     *  are FRACTIONS of the dock container. `min*`/`max*` (px) CLAMP that
     *  initial size — they never restrict the user's later resizing, and
     *  only the group's LEAD tab participates (see {@link groupLeadPanels};
     *  a hidden tab's size must not bind its group). */
    size?: {
        width?: number; height?: number;
        minWidth?: number; minHeight?: number;
        maxWidth?: number; maxHeight?: number;
    };
}

export interface WorkspaceDefinition {
    /** STABLE storage key (index entry, layout blob, seen-version,
     *  activeWorkspace) — never change it once shipped; users' stored state
     *  is keyed by it. User-created workspaces use their typed name as id. */
    id: string;
    /** DISPLAY name (tab strip + picker) — free to change any time */
    name: string;
    /** BUMP only when the recipe is RESTRUCTURED — seeding then DROPS a user's
     *  saved arrangement of this workspace so the new recipe rebuilds.
     *
     *  ADDING a panel needs no bump: the dock reconciles a restored layout
     *  against the recipe and injects what is missing, once each, keeping the
     *  arrangement (workspaceReconcile.ts). Renaming a panel's tab needs none
     *  either — the reconcile fixes drifted titles too. Reach for a bump only
     *  when positions/groups actually change, and know that it costs every
     *  user their layout for this workspace. */
    version: number;
    /** tab order among built-ins */
    order: number;
    /** explicit panel list — nothing else exists in this workspace */
    panels: WorkspacePanelSpec[];
    /** which tab leads each tabbed group (dockview fronts the newest
     *  "within" insert otherwise) */
    activePanels?: DockPanelId[];
}

/** "Default" is special everywhere (undeletable, legacy-layout migration).
 *  This is the ID (storage key) — the display name lives on the definition. */
export const DEFAULT_WORKSPACE_NAME = "Default";

export const BUILTIN_WORKSPACES: readonly WorkspaceDefinition[] = [
    {
        id: DEFAULT_WORKSPACE_NAME,
        name: "Default",
        // 3: added the Mesh Baker panel. 4: renamed its tab. Both were paid for
        // with a bump — i.e. with everyone's layout — because that was the only
        // lever there was, and it still reached nobody: an updating user's blob
        // shadowed the recipe. Neither would need a bump today; the reconcile
        // handles additions and renames. Left at 4, not bumped again.
        version: 4,
        order: 0,
        panels: [
            { id: "scene", title: "Scene" },
            { id: "hierarchy", title: "Hierarchy", position: { referencePanel: "scene", direction: "left" }, size: { width: 320, minWidth: 240 } },
            { id: "assets", title: "Assets", position: { referencePanel: "hierarchy", direction: "within" }, size: { height: 260, minHeight: 160 } },

            { id: "inspector", title: "Inspector", position: { referencePanel: "scene", direction: "right" }, size: { width: 0.1, minWidth: 400 } },
            { id: "resources", title: "Resources", position: { referencePanel: "inspector", direction: "left" }, size: { width: 0.1, minWidth: 300 } },
            { id: "performance", title: "Performance", position: { referencePanel: "inspector", direction: "within" } },
            { id: "bakegroups", title: "Mesh Baker", position: { referencePanel: "inspector", direction: "within" } },

            { id: "aichat", title: "AI Chat", position: { referencePanel: "scene", direction: "below" }, size: { maxHeight: 400 } },
            { id: "projects", title: "Projects", position: { referencePanel: "aichat", direction: "within" } },
            { id: "code", title: "Code", position: { referencePanel: "aichat", direction: "within" } },
            { id: "nodegraph", title: "Node Graph", position: { referencePanel: "aichat", direction: "within" } },
            { id: "components", title: "Components", position: { referencePanel: "aichat", direction: "within" } },
            // sits with Projects: both answer "what is this project, and where
            // did it go" (the reconcile injects it into existing layouts)
            { id: "deployment", title: "Deployment", position: { referencePanel: "aichat", direction: "within" } },
        ],
        activePanels: ["hierarchy", "aichat", "inspector", "resources"],
    },
    {
        id: "ai",
        name: "Agentic",
        version: 1,
        order: 1,
        panels: [
            { id: "scene", title: "Scene" },
            // fractions, not px: 420+680+280 fixed crushed the scene to its
            // minimum on a 1280px screen — content panels scale instead
            { id: "aichat", title: "AI Chat", position: { referencePanel: "scene", direction: "left" }, size: { width: 0.2, minWidth: 260 } },
            { id: "performance", title: "Performance", position: { referencePanel: "scene", direction: "right" }, size: { width: 420, minWidth: 300 } },
            { id: "resources", title: "Resources", position: { referencePanel: "performance", direction: "within" } },
        ],
        activePanels: ["aichat", "performance"],
    },
    {
        id: "Performance",
        name: "Performance",
        version: 1,
        order: 2,
        panels: [
            { id: "scene", title: "Scene" },
            // the tabs share ONE group — its size comes from the first panel;
            // per-tab widths on "within" siblings would be redundant
            { id: "performance", title: "Performance", position: { referencePanel: "scene", direction: "right" }, size: { width: 0.2, minWidth: 300 } },
            { id: "resources", title: "Resources", position: { referencePanel: "performance", direction: "within" } },
            { id: "aichat", title: "AI Chat", position: { referencePanel: "performance", direction: "within" } },
            { id: "inspector", title: "Inspector", position: { referencePanel: "performance", direction: "within" } },
        ],
        activePanels: ["performance"],
    },
    {
        id: "shots",
        name: "Animation",
        version: 5,
        order: 3,
        panels: [
            { id: "scene", title: "Scene" },
            { id: "timeline", title: "Timeline", position: { referencePanel: "scene", direction: "below" }, size: { height: 0.25, minHeight: 120 } },
            { id: "inspector", title: "Inspector", position: { referencePanel: "scene", direction: "right" }, size: { width: 340, minWidth: 280 } },
            { id: "hierarchy", title: "Hierarchy", position: { referencePanel: "inspector", direction: "within" } },
        ],
        activePanels: ["inspector", "timeline"],
    },
];

/**
 * Structural soundness of a recipe — returns human-readable problems (empty
 * = valid). Checked by tests for every built-in; the dock may also assert in
 * dev. Rules: at least one panel; unique KNOWN panel ids; exactly the first
 * panel is positionless (the root); every referencePanel points at an
 * EARLIER panel (addPanel order); activePanels reference listed panels.
 */
export function validateWorkspaceDefinition(definition: WorkspaceDefinition): string[] {
    const problems: string[] = [];
    if (!definition.id?.trim()) problems.push("workspace has no id (the stable storage key)");
    if (!definition.name?.trim()) problems.push("workspace has no display name");
    if (!(definition.version >= 1)) problems.push("workspace needs a version >= 1");
    if (!definition.panels?.length) {
        problems.push("workspace lists no panels");
        return problems;
    }
    const seen = new Set<string>();
    definition.panels.forEach((panel, index) => {
        if (!DOCK_PANEL_IDS.includes(panel.id)) problems.push(`unknown panel id "${panel.id}"`);
        if (seen.has(panel.id)) problems.push(`duplicate panel "${panel.id}"`);
        if (index === 0 && panel.position) problems.push(`the first panel ("${panel.id}") is the root and must not have a position`);
        if (index > 0 && !panel.position) problems.push(`panel "${panel.id}" needs a position (only the root goes without)`);
        if (panel.position && !seen.has(panel.position.referencePanel)) {
            problems.push(`panel "${panel.id}" references "${panel.position.referencePanel}" before it exists`);
        }
        // value check, not just types: vitest doesn't typecheck, so a bad
        // direction string ("inside") would otherwise reach dockview
        if (panel.position && !["left", "right", "above", "below", "within"].includes(panel.position.direction)) {
            problems.push(`panel "${panel.id}" has unknown direction "${panel.position.direction}" (left|right|above|below|within)`);
        }
        // size sanity: min ≤ intended ≤ max per axis (a violation would
        // silently clamp at runtime and the authored size would never show).
        // Values ≤ 1 are container FRACTIONS — different unit, so they only
        // get a range check, not the px comparison against min/max.
        const size = panel.size;
        for (const [axis, min, value, max] of [
            ["width", size?.minWidth, size?.width, size?.maxWidth],
            ["height", size?.minHeight, size?.height, size?.maxHeight],
        ] as const) {
            if (min !== undefined && max !== undefined && min > max) problems.push(`panel "${panel.id}": min ${axis} ${min} exceeds max ${max}`);
            if (value !== undefined && value <= 0) problems.push(`panel "${panel.id}": ${axis} ${value} must be positive`);
            const fractional = value !== undefined && value <= 1;
            if (!fractional && value !== undefined && min !== undefined && value < min) problems.push(`panel "${panel.id}": ${axis} ${value} is below its min ${min}`);
            if (!fractional && value !== undefined && max !== undefined && value > max) problems.push(`panel "${panel.id}": ${axis} ${value} exceeds its max ${max}`);
        }
        seen.add(panel.id);
    });
    for (const active of definition.activePanels ?? []) {
        if (!seen.has(active)) problems.push(`activePanels names "${active}" which is not in the workspace`);
    }
    return problems;
}

/**
 * The panels that LEAD their tab group: the `activePanels` entry of each
 * group, else the group root ("within" chains collapse onto the first
 * non-within anchor). Only leads contribute size/constraints at build —
 * a hidden tab must not bind the group it shares (Marcel 2026-08-19).
 */
export function groupLeadPanels(definition: WorkspaceDefinition): Set<DockPanelId> {
    const rootOf = new Map<DockPanelId, DockPanelId>();
    for (const panel of definition.panels) {
        const position = panel.position;
        rootOf.set(panel.id, position?.direction === "within"
            ? rootOf.get(position.referencePanel) ?? position.referencePanel
            : panel.id);
    }
    const leadByRoot = new Map<DockPanelId, DockPanelId>();
    for (const panel of definition.panels) {
        const root = rootOf.get(panel.id)!;
        if (!leadByRoot.has(root)) leadByRoot.set(root, root);
    }
    for (const active of definition.activePanels ?? []) {
        const root = rootOf.get(active);
        if (root) leadByRoot.set(root, active);
    }
    return new Set(leadByRoot.values());
}

/** the built-in definition for a workspace ID (the storage key), if any */
export type WorkspaceSurface = 'embedded' | 'popout';

/** Popouts contain tools only; the inspected canvas remains on the host page. */
export function popoutWorkspaceDefinition(definition: WorkspaceDefinition): WorkspaceDefinition {
    if (definition.id === DEFAULT_WORKSPACE_NAME) {
        return {
            ...definition,
            version: 1,
            panels: [
                { id: 'hierarchy', title: 'Hierarchy', size: { width: 0.3, minWidth: 220 } },
                { id: 'inspector', title: 'Inspector', position: { referencePanel: 'hierarchy', direction: 'right' } },
                { id: 'resources', title: 'Resources', position: { referencePanel: 'inspector', direction: 'below' }, size: { height: 0.4, minHeight: 180 } },
                ...definition.panels.filter(panel => !['scene', 'hierarchy', 'inspector', 'resources'].includes(panel.id))
                    .map(panel => ({ id: panel.id, title: panel.title, position: { referencePanel: 'resources' as const, direction: 'within' as const } })),
            ],
            activePanels: ['hierarchy', 'inspector', 'resources'],
        };
    }
    const panels = definition.panels.filter(panel => panel.id !== 'scene');
    const root = panels[0].id;
    return {
        ...definition,
        version: 1,
        panels: panels.map((panel, index) => ({
            ...panel,
            position: index === 0 ? undefined : panel.position?.referencePanel === 'scene'
                ? { ...panel.position, referencePanel: root } : panel.position,
        })),
    };
}

export const POPOUT_WORKSPACES: readonly WorkspaceDefinition[] = BUILTIN_WORKSPACES.map(popoutWorkspaceDefinition);

export function builtinWorkspace(id: string, surface: WorkspaceSurface = 'embedded'): WorkspaceDefinition | undefined {
    return (surface === 'popout' ? POPOUT_WORKSPACES : BUILTIN_WORKSPACES).find(definition => definition.id === id);
}

/** DISPLAY name for a workspace storage id: a built-in's authored name, the
 *  id itself for user-created workspaces (their typed name IS their id).
 *  The one resolver the tab strip and the picker share. */
export function workspaceDisplayName(id: string): string {
    return builtinWorkspace(id)?.name ?? id;
}
